import { isMockServices } from 'src/utils';
import mock from 'src/services/utils/mock';
import { QueryFunction } from '@tanstack/react-query';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';

export interface GetIncomeClarityReviewTasksParams {
  loanId: string;
}

export interface IncomeClarityReviewTask {
  id: string;
  name: string;
  status: string;
  priority: string;
  targetCompletionDate?: Date | null;
  assignedToUserId?: string;
  documentId?: string;
  extractedDataId?: string;
  comment?: string;
  taskType?: string;
  createdAt: Date;
  updatedAt: Date;
  isDeleted: boolean;
  assignedTo?: {
    id: string;
    name: string;
  } | null;
  document?: {
    documentType: {
      id: string;
      name: string;
      displayName: string;
    };
    metadata?: {
      external_document_filename?: string;
    };
  };
  extractedData?: {
    id: string;
    fieldType: {
      displayName: string;
      documentTypeId: string;
    };
    initialValue?: string;
    fieldConfidence: number;
  };
}

export interface GetIncomeClarityReviewTasksResponse {
  success: boolean;
  tasks: IncomeClarityReviewTask[];
  nextCursor?: string;
}

// Helper function to reduce duplication
const createMockIncomeClarityReviewTask = (
  id: string,
  name: string,
  status: string,
  documentId: string,
  extractedDataId: string,
  comment: string,
  assignedToName: string,
): IncomeClarityReviewTask => ({
  id,
  name,
  status,
  priority: 'high',
  targetCompletionDate: new Date('2024-12-31'),
  assignedToUserId: 'user-1',
  documentId,
  extractedDataId,
  comment,
  taskType: 'incomeClarityReview',
  createdAt: new Date('2024-12-01'),
  updatedAt: new Date('2024-12-01'),
  isDeleted: false,
  assignedTo: {
    id: 'user-1',
    name: assignedToName,
  },
  document: {
    documentType: {
      id: 'w2-type',
      name: 'W-2',
      displayName: 'W-2 Form',
    },
  },
  extractedData: {
    id: extractedDataId,
    fieldType: {
      displayName: 'Annual Income',
      documentTypeId: 'w2-type',
    },
    initialValue: '$75,000',
    fieldConfidence: 85,
  },
});

const getIncomeClarityReviewTasks: QueryFunction<
  GetIncomeClarityReviewTasksResponse,
  ['/income-clarity-review-tasks', GetIncomeClarityReviewTasksParams]
> = async ({ queryKey }) => {
  if (isMockServices()) {
    return await mock({
      success: true,
      tasks: [
        createMockIncomeClarityReviewTask(
          'task-1',
          'Income Clarity Review - W-2 Document',
          'open',
          'doc-1',
          'ext-1',
          'Review W-2 income data for accuracy',
          'John Doe',
        ),
      ],
      nextCursor: undefined,
    });
  }

  const params = queryKey[1];
  const queryParams = new URLSearchParams();
  queryParams.append('loanId', params.loanId);
  queryParams.append('taskType', 'incomeClarityReview');

  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<GetIncomeClarityReviewTasksResponse>(
    `/userTasks?${queryParams.toString()}`,
    {
      method: 'GET',
      paginate: false,
    },
  );

  if (response.data) return response.data;
  throw new Error('Failed to fetch income clarity review tasks');
};

export default getIncomeClarityReviewTasks;
